import moment from 'moment';

import { Section } from '../../../../../Model/CoachNotificationView';
import { formatRelativeTime } from '../../../../../utils/date';

import { checkIsPriorityNotification } from './notificationPriority';

const SECTIONS = Object.values(Section);

const UNKNOWN_WAIT_SORT_KEY = Number.MAX_SAFE_INTEGER;

const toOpenSections = (view) => SECTIONS
  .filter((section) => view[section]?.unreadCount > 0)
  .map((section) => ({
    section,
    oldestNotificationAtSeconds: view[section].oldestNotificationAt?.seconds ?? null,
    isClaimed: !!view[section].claimedBy,
  }));

const getLongestWaitingSection = (openSections) => openSections
  .reduce((longestWaiting, candidate) => {
    if (!candidate.oldestNotificationAtSeconds) {
      return longestWaiting;
    }
    return !longestWaiting || candidate.oldestNotificationAtSeconds < longestWaiting.oldestNotificationAtSeconds
      ? candidate
      : longestWaiting;
  }, null) ?? openSections[0] ?? null;

/**
 * Summarizes a pool coach's outstanding work. The wait is measured over every open section,
 * claimed or not - a claim never expires, so an item held by an AC who moved on has to keep
 * ageing in plain sight for someone else to take it over.
 */
const summarizeOpenWork = (view) => {
  const openSections = toOpenSections(view);
  const longestWaitingSection = getLongestWaitingSection(openSections);

  return {
    hasOpenWork: !!openSections.length,
    openSectionCount: openSections.length,
    unclaimedSectionCount: openSections.filter(({ isClaimed }) => !isClaimed).length,
    longestWaitingSection,
    oldestOpenAtSeconds: longestWaitingSection?.oldestNotificationAtSeconds ?? null,
    isOldestOpenPriority: checkIsPriorityNotification(
      longestWaitingSection?.oldestNotificationAtSeconds,
      longestWaitingSection?.section,
    ),
  };
};

const getWaitSortKey = (summary) => summary?.oldestOpenAtSeconds ?? UNKNOWN_WAIT_SORT_KEY;

const getLongestWaitingSummary = (summaries) => summaries
  .reduce((longestWaiting, candidate) => {
    if (!candidate.oldestOpenAtSeconds) {
      return longestWaiting;
    }
    return !longestWaiting || candidate.oldestOpenAtSeconds < longestWaiting.oldestOpenAtSeconds
      ? candidate
      : longestWaiting;
  }, null);

const summarizePoolQueue = (views) => {
  const summaryByCoachId = new Map(views.map((view) => [view.id, summarizeOpenWork(view)]));
  const summaries = [...summaryByCoachId.values()];
  const longestWaitingSummary = getLongestWaitingSummary(summaries);

  const orderedViews = views
    .filter((view) => summaryByCoachId.get(view.id).hasOpenWork)
    .sort((viewA, viewB) => (
      getWaitSortKey(summaryByCoachId.get(viewA.id)) - getWaitSortKey(summaryByCoachId.get(viewB.id))
      || (viewA.name ?? '').localeCompare(viewB.name ?? '')
    ));

  return {
    orderedViews,
    summaryByCoachId,
    stats: {
      coachCount: views.length,
      coachesWithOpenWorkCount: orderedViews.length,
      openItemCount: summaries.reduce((total, { openSectionCount }) => total + openSectionCount, 0),
      unclaimedItemCount: summaries.reduce((total, { unclaimedSectionCount }) => total + unclaimedSectionCount, 0),
      oldestOpenAtSeconds: longestWaitingSummary?.oldestOpenAtSeconds ?? null,
      isOldestOpenPriority: !!longestWaitingSummary?.isOldestOpenPriority,
    },
  };
};

const getWaitInDays = (oldestNotificationAtSeconds) => (
  moment().diff(moment.unix(oldestNotificationAtSeconds), 'days')
);

const formatWaitTime = (oldestNotificationAtSeconds) => (
  oldestNotificationAtSeconds ? formatRelativeTime(moment.unix(oldestNotificationAtSeconds)) : null
);

export {
  formatWaitTime,
  getWaitInDays,
  getWaitSortKey,
  summarizePoolQueue,
};
